import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';

const MENU_WIDTH = 232;
const VIEWPORT_MARGIN = 8;

/**
 * Menú de fila que se renderiza en un portal con `position: fixed`.
 *
 * Un dropdown `absolute` dentro de la tabla queda recortado: DataTable tiene
 * `overflow-hidden` en la tarjeta y `overflow-x-auto` en el scroller, así que
 * el menú de las últimas filas se corta. El portal escapa de ambos.
 *
 * Usar como `rowActions` de DataTable en vez de un dropdown `absolute` propio.
 */
export const RowActionsMenu: React.FC<{ children: (close: () => void) => React.ReactNode }> = ({
  children,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const close = () => setOpen(false);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 0;

    // Abre hacia arriba si abajo no entra (última fila de la tabla).
    const opensUpward = rect.bottom + menuHeight + VIEWPORT_MARGIN > window.innerHeight;

    setPosition({
      top: opensUpward ? Math.max(VIEWPORT_MARGIN, rect.top - menuHeight - 4) : rect.bottom + 4,
      left: Math.max(VIEWPORT_MARGIN, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN)),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        !menuRef.current?.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // El menú es fixed: si la página o la tabla scrollean queda desalineado,
    // así que se cierra en vez de perseguir la posición.
    const handleScroll = () => setOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Acciones"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
      >
        <MoreVertical size={18} />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{ top: position.top, left: position.left, width: MENU_WIDTH }}
            className="fixed z-50 bg-white rounded-lg shadow-lg border border-slate-200 py-1"
            onClick={(e) => e.stopPropagation()}
          >
            {children(close)}
          </div>,
          document.body
        )}
    </>
  );
};
